// Composite-on-change orchestrator for the `big-3-hero-image` Block
// (P-037.28 / D-136). Sibling to `big3-composite-orchestrator.tsx`
// (D-132). Renders as an invisible side-effect component inside
// `<Form>` whenever `blockId === 'big-3-hero-image'`. Watches
// `imageUrl` + `bigHeroLogoUrl`; when either changes (and at least
// one is set), runs `compositeBigHero` → uploads the resulting PNG →
// writes the resulting URL back into `compositeImageUrl` via the
// Form's `onChange` chain.
//
// **Same race + staleness + lazy-firebase patterns as D-132**:
// `fetchTokenRef` sequence number drops stale resolutions; `latestRef`
// assigned during render ensures merge-into-latest-state semantics;
// dynamic `await import('firebase-storage')` via the shared
// `createLazyComponentLoader` factory keeps the SDK out of the initial
// bundle. The 4th consumer of the createLazyComponentLoader factory
// (P-FS-05 / D-122).
//
// **Single-instance Block, not array-of-items.** big-3-hero-image's
// inputs are flat (imageUrl + bigHeroLogoUrl + compositeImageUrl);
// the orchestrator runs ONE effect, not a per-item map. Simpler than
// the D-132 sibling (no Big3ItemEffect wrapper needed).
//
// **Skip both-empty.** When neither imageUrl nor bigHeroLogoUrl is
// set, the orchestrator does NOT generate a both-fallbacks composite
// (the Block's autoHide returns true for both-empty so the Block
// itself omits — no composite needed). On clear (sources transition
// set → unset), the orchestrator strips any stale compositeImageUrl
// so the Block's autoHide path stays consistent.
//
// **Surfaced errors.** Same Retry-toast pattern as D-132 — bumps
// `retryNonce` → re-fires the effect with a fresh job key.

import { useEffect, useRef, useState } from 'preact/hooks'
import type { ComponentChild } from 'preact'

import { compositeBigHero, isCompositeSupported } from '../../../lib/image-composite'
import { addToast } from '../../toast/toast-store'
import { errorMessage } from '../../../lib/error'
import { FALLBACK_IMAGE_RAW_SRC, FALLBACK_LOGO_SRC } from '../../../components/helpers'
import { createLazyComponentLoader } from '../fields/lazy-component'

const BLOCK_ID = 'big-3-hero-image'

// Lazy firebase-storage module loader — shares the same factory as the
// D-132 sibling. The two orchestrators each cache their own loader
// instance; both resolve to the same module on first import, then
// Vite serves the shared firebase chunk thereafter.
const firebaseStorageLoader = createLazyComponentLoader(
  () => import('../../../lib/firebase-storage'),
  (m) => m,
)
export const resetFirebaseStorageCacheForTest = firebaseStorageLoader.resetForTest

interface BlockInputs {
  readonly imageUrl?: string
  readonly bigHeroLogoUrl?: string
  readonly compositeImageUrl?: string
  readonly [key: string]: unknown
}

interface OrchestratorProps {
  readonly value: unknown
  readonly onChange: (next: unknown) => void
}

export function BigHeroOrchestrator({ value, onChange }: OrchestratorProps): ComponentChild {
  // Stable ref to the latest value + onChange. Assigned DURING render —
  // protects against the user editing unrelated fields mid-upload.
  const latestRef = useRef<{ value: unknown; onChange: (next: unknown) => void }>({
    value,
    onChange,
  })
  latestRef.current = { value, onChange }

  const inputs = (value ?? {}) as BlockInputs
  const eventSrc = normalizeSource(inputs.imageUrl)
  const logoSrc = normalizeSource(inputs.bigHeroLogoUrl)
  const compositeImageUrl = inputs.compositeImageUrl

  // Retry nonce — Retry toast bumps it to re-trigger the effect after
  // a failure.
  const [retryNonce, setRetryNonce] = useState(0)
  const sourceKey = `${eventSrc ?? ''}|${logoSrc ?? ''}`
  const jobKey = `${sourceKey}|retry:${retryNonce}`
  // Token sequence — bumped on every kick-off + on unmount. Async
  // resolutions check token vs current to drop stale work.
  const fetchTokenRef = useRef(0)
  // The jobKey for which the LAST compose successfully wrote a
  // compositeImageUrl. `null` = no compose has run yet OR a JSON-
  // loaded state's pre-existing composite.
  const lastJobRef = useRef<string | null>(null)

  useEffect(() => {
    const haveSource = eventSrc !== undefined || logoSrc !== undefined

    // Clear-stale arm: sources fully cleared but a stale composite
    // persists → strip it.
    if (!haveSource) {
      if (compositeImageUrl !== undefined) {
        const current = latestRef.current.value as BlockInputs | undefined
        const next = { ...(current ?? {}) }
        delete next.compositeImageUrl
        latestRef.current.onChange(next)
      }
      lastJobRef.current = jobKey
      return
    }

    // Skip when this exact (sources × retryNonce) job already completed.
    if (lastJobRef.current === jobKey) return

    // Initial mount with pre-existing composite (JSON-loaded state):
    // record the jobKey + trust the persisted composite. No upload.
    if (lastJobRef.current === null && compositeImageUrl !== undefined) {
      lastJobRef.current = jobKey
      return
    }

    // Browser-support guard. Silent fallback when Canvas APIs aren't
    // available — same discipline as D-132 / D-121.
    if (!isCompositeSupported()) {
      lastJobRef.current = jobKey
      return
    }

    const token = ++fetchTokenRef.current

    void (async () => {
      try {
        const result = await compositeBigHero({
          eventImageUrl: eventSrc,
          big3LogoUrl: logoSrc,
          // Post-Phase-Close §item G — composite gets the RAW (un-baked)
          // fallback. The composite Canvas bakes the inset shadow itself
          // on the event-image region of the final composite PNG; using
          // the pre-baked `FALLBACK_IMAGE_SRC` here would double-apply
          // on the rare composite-fallback case (chapter forgot to set
          // event image).
          fallbackEventImageUrl: FALLBACK_IMAGE_RAW_SRC,
          fallbackLogoUrl: FALLBACK_LOGO_SRC,
        })
        if (token !== fetchTokenRef.current) return
        const { uploadCompositeImage } = await firebaseStorageLoader.load()
        if (token !== fetchTokenRef.current) return
        const upload = await uploadCompositeImage(result.blob, BLOCK_ID)
        if (token !== fetchTokenRef.current) return
        const current = latestRef.current.value as BlockInputs | undefined
        latestRef.current.onChange({ ...(current ?? {}), compositeImageUrl: upload.url })
        lastJobRef.current = jobKey
      } catch (e) {
        if (token !== fetchTokenRef.current) return
        addToast({
          kind: 'error',
          message: errorMessage(e, 'Could not generate the Big 3 hero composite.'),
          action: {
            label: 'Retry',
            onClick: () => setRetryNonce((n) => n + 1),
          },
        })
      }
    })()

    return () => {
      fetchTokenRef.current += 1
    }
  }, [jobKey, compositeImageUrl])

  return null
}

/** Treat empty string + whitespace-only same as undefined.
 *  `<ImageUrlField>`'s Clear button dispatches `onChange('')` so the
 *  persisted state has `imageUrl: ''` rather than `imageUrl: undefined`.
 *  Same normalization as the D-132 sibling. */
function normalizeSource(url: string | undefined): string | undefined {
  if (url === undefined) return undefined
  const trimmed = url.trim()
  return trimmed === '' ? undefined : trimmed
}
